// @ts-nocheck
/**
 * App Lifecycle Service
 * Handles iOS app state changes (foreground/background) and background fetch
 */

import { Capacitor } from '@capacitor/core';
import { App } from '@capacitor/app';
import logger from '../../utils/logger';

const IS_NATIVE = Capacitor.isNativePlatform();

/**
 * App lifecycle event handlers
 */
let stateChangeListener = null;
let urlOpenListener = null;
let backButtonListener = null;

/**
 * Initialize app lifecycle listeners
 * @param {{ onForeground?: () => void, onBackground?: () => void, onUrlOpen?: (url: string) => void }} handlers
 */
export async function initAppLifecycle(handlers = {}) {
  if (!IS_NATIVE) {
    return;
  }

  const { onForeground, onBackground, onUrlOpen } = handlers;

  try {
    // Listen for app state changes (foreground/background)
    stateChangeListener = await App.addListener('appStateChange', (state) => {
      if (state.isActive) {
        logger.info('App moved to foreground');
        onForeground?.();
      } else {
        logger.info('App moved to background');
        onBackground?.();
      }
    });

    // Listen for URL opens (deep links, universal links)
    urlOpenListener = await App.addListener('appUrlOpen', (event) => {
      logger.info('App URL opened:', event.url);
      onUrlOpen?.(event.url);
    });

    // Handle back button on Android (no-op on iOS but good to have)
    backButtonListener = await App.addListener('backButton', () => {
      // On iOS this won't fire, but on Android it will
      logger.info('Back button pressed');
    });

    logger.info('App lifecycle listeners initialized');

    // Check for launch URL (cold start deep link)
    const launchUrl = await App.getLaunchUrl();
    if (launchUrl?.url) {
      logger.info('App launched with URL:', launchUrl.url);
      onUrlOpen?.(launchUrl.url);
    }
  } catch (error) {
    logger.warn('Failed to initialize app lifecycle:', error);
  }
}

/**
 * Clean up app lifecycle listeners
 */
export async function cleanupAppLifecycle() {
  try {
    await stateChangeListener?.remove();
    await urlOpenListener?.remove();
    await backButtonListener?.remove();
    stateChangeListener = null;
    urlOpenListener = null;
    backButtonListener = null;
  } catch (error) {
    logger.warn('Failed to cleanup app lifecycle:', error);
  }
}

/**
 * Get app info (version, build, etc.)
 * @returns {Promise<{ name: string, id: string, build: string, version: string } | null>}
 */
export async function getAppInfo() {
  if (!IS_NATIVE) {
    return null;
  }

  try {
    return await App.getInfo();
  } catch (error) {
    logger.warn('Failed to get app info:', error);
    return null;
  }
}

/**
 * Get app state (active/inactive)
 * @returns {Promise<boolean>}
 */
export async function isAppActive() {
  if (!IS_NATIVE) {
    return !document.hidden;
  }

  try {
    const state = await App.getState();
    return state.isActive;
  } catch (error) {
    return true; // Assume active on error
  }
}

/**
 * Exit the app (Android only, no-op on iOS)
 */
export async function exitApp() {
  if (!IS_NATIVE) {
    return;
  }

  try {
    await App.exitApp();
  } catch (error) {
    logger.warn('Failed to exit app:', error);
  }
}

export default {
  initAppLifecycle,
  cleanupAppLifecycle,
  getAppInfo,
  isAppActive,
  exitApp,
};
